import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "newsBannerDismissedAt";

function getSnapshot() {
  return localStorage.getItem(STORAGE_KEY);
}

function subscribe(callback: () => void) {
  window.addEventListener("storage", callback);
  return () => void window.removeEventListener("storage", callback);
}

/**
 * Hook that controls news banner visibility.
 *
 * The banner is visible when:
 * 1. The current time is before the `showUntil`.
 * 2. The user has not dismissed it since `publishedAt`.
 *
 * Storing a UNIX timestamp allows future banners to re-appear automatically
 * without adding new localStorage keys — a new `publishedAt` that is after
 * the stored dismissal timestamp will cause the banner to show again.
 */
export function useNewsBanner(publishedAt: Date, showUntil: Date) {
  const dismissedAt = useSyncExternalStore(subscribe, getSnapshot);

  const isExpired = Date.now() >= showUntil.getTime();

  const isDismissed =
    dismissedAt !== null && Number(dismissedAt) >= publishedAt.getTime();

  const isVisible = !isExpired && !isDismissed;

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    // Trigger re-render for same-tab listeners.
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }, []);

  return { isVisible, dismiss } as const;
}
